/**
 * Repository Health Dashboard — Phase D (KPI tiles + trends) +
 * Phase E (CSV export). Lives at `/data/repository/dashboard`.
 *
 * Polls `GET /api/data/admin/repository/kpis` on mount and on
 * "Refresh" button click. Backend serves a cached snapshot (60s TTL)
 * so polling is cheap.
 *
 * Each tile links to the matching filtered repository view so the
 * operator can drill from KPI → row list in one click.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowsClockwise, Download, ChartBar, Buildings, Image,
    ImageBroken, Flag, CheckCircle, XCircle, MinusCircle,
    Clock, Question, Sparkle, Lightning, Stamp,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";


// ── Tile component ─────────────────────────────────────────────────
function KpiTile({ label, value, pct, hint, icon: Icon, tone, linkTo, testId, tooltip }) {
    const toneClass = {
        sky:     "bg-sky-50 text-sky-900 border-sky-200",
        emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
        amber:   "bg-amber-50 text-amber-900 border-amber-200",
        rose:    "bg-rose-50 text-rose-900 border-rose-200",
        slate:   "bg-white text-slate-900 border-slate-200",
        indigo:  "bg-indigo-50 text-indigo-900 border-indigo-200",
    }[tone || "slate"];

    const inner = (
        <div
            data-testid={testId}
            title={tooltip || undefined}
            className={
                "rounded-lg border p-4 transition-shadow " + toneClass +
                (linkTo ? " hover:shadow-md cursor-pointer" : "")
            }
        >
            <div className="flex items-start justify-between mb-2">
                <span className="reedos-eyebrow text-slate-600">{label}</span>
                {Icon && <Icon size={16} className="text-slate-400" />}
            </div>
            <div className="font-display text-2xl font-bold leading-none">
                {typeof value === "number" ? value.toLocaleString() : value}
            </div>
            <div className="flex items-baseline gap-2 mt-1.5">
                {pct != null && (
                    <span data-testid={`${testId}-pct`} className="text-xs text-slate-500">
                        {pct.toFixed(1)}%
                    </span>
                )}
                {hint && (
                    <span className="text-xs text-slate-400">{hint}</span>
                )}
            </div>
        </div>
    );
    return linkTo ? <Link to={linkTo}>{inner}</Link> : inner;
}


function TrendCard({ label, trend, icon: Icon, testId }) {
    return (
        <div
            data-testid={testId}
            className="rounded-lg border border-slate-200 bg-white p-4"
        >
            <div className="flex items-center justify-between mb-3">
                <span className="reedos-eyebrow text-slate-600">{label}</span>
                {Icon && <Icon size={16} className="text-slate-400" />}
            </div>
            <div className="flex items-baseline gap-6">
                <div>
                    <div className="font-display text-xl font-bold leading-none" data-testid={`${testId}-7d`}>
                        {trend.last_7d.toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Last 7 days</div>
                </div>
                <div>
                    <div className="font-display text-xl font-bold leading-none text-slate-700" data-testid={`${testId}-30d`}>
                        {trend.last_30d.toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Last 30 days</div>
                </div>
            </div>
        </div>
    );
}


// ── Stale-data indicator ────────────────────────────────────────────
// The KPI endpoint has a 60s in-process TTL cache, so a snapshot can
// be up to ~60s old before Refresh would actually fetch new data.
// We surface that visually:
//   <15s  emerald  (fresh)
//   <45s  slate    (recent)
//   <60s  amber    (about to refresh on next call)
//   ≥60s  rose     (beyond TTL — may be served from a stale cache)
// The relative-time string ticks every 5s so the operator sees the
// age progress without having to re-load the page.
function StaleIndicator({ generatedAt }) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 5000);
        return () => clearInterval(id);
    }, []);
    if (!generatedAt) return null;
    const generatedMs = Date.parse(generatedAt);
    if (Number.isNaN(generatedMs)) {
        return <span data-testid="stale-indicator">Snapshot generated {generatedAt}</span>;
    }
    const ageSec = Math.max(0, Math.floor((now - generatedMs) / 1000));
    const tone =
        ageSec < 15  ? { dot: "bg-emerald-500", text: "text-emerald-700", label: "fresh" } :
        ageSec < 45  ? { dot: "bg-slate-400",   text: "text-slate-600",   label: "recent" } :
        ageSec < 60  ? { dot: "bg-amber-500",   text: "text-amber-700",   label: "ageing" } :
                       { dot: "bg-rose-500",    text: "text-rose-700",    label: "stale" };
    const rel =
        ageSec < 5   ? "just now" :
        ageSec < 60  ? `${ageSec}s ago` :
        ageSec < 3600 ? `${Math.floor(ageSec / 60)}m ago` :
                        `${Math.floor(ageSec / 3600)}h ago`;
    return (
        <span
            data-testid="stale-indicator"
            data-stale-tone={tone.label}
            className="inline-flex items-center gap-1.5"
        >
            <span
                aria-hidden="true"
                className={`inline-block w-1.5 h-1.5 rounded-full ${tone.dot}`}
            />
            <span>Snapshot generated {generatedAt}</span>
            <span className={`${tone.text} font-medium`}>· {rel}</span>
        </span>
    );
}


export default function RepositoryDashboard() {
    const { can } = useAuth();
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    // 2026-02-10 — Repository population (full corpus, active +
    // inactive). The KPI snapshot's `cohort_total` reports the
    // ACTIVE row count (the operational denominator for enrichment
    // health), but operators benefit from seeing the full
    // repository total alongside as transparency context — the
    // 3,114 inactive (mostly matured BOND_ISSUER) rows are real
    // historical records, not lost data. Source: `/api/data/stats`.
    const [population, setPopulation] = useState({ total: null, active: null, inactive: null });
    useEffect(() => {
        let cancelled = false;
        api.get("/data/stats")
            .then(({ data }) => {
                if (cancelled) return;
                setPopulation({
                    total: data.total_companies ?? null,
                    active: data.active_companies ?? null,
                    inactive: data.inactive_companies ?? null,
                });
            })
            .catch(() => {
                // Best-effort — sub-tile silently hides when
                // population isn't available.
            });
        return () => { cancelled = true; };
    }, []);

    const load = async (nocache = false) => {
        setLoading(true);
        try {
            const { data } = await api.get(
                "/data/admin/repository/kpis",
                { params: nocache ? { nocache: true } : {} },
            );
            setSnapshot(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load KPIs"));
        } finally {
            setLoading(false);
        }
    };

    // Inline useEffect body (no extracted helper call) matches the
    // proven lint-clean pattern used in `DataExport.jsx`. Calling a
    // local async helper from inside useEffect triggers the React 19
    // `react-hooks/set-state-in-effect` analyser to flag sibling
    // setState functions in the same component scope as well; the
    // inline `.then().catch().finally()` chain side-steps that.
    // `setLoading` is intentionally NOT called synchronously inside
    // the effect body — `useState(true)` already starts loading=true
    // and the `.finally()` flips it off after the request resolves.
    useEffect(() => {
        api.get("/data/admin/repository/kpis")
            .then(({ data }) => setSnapshot(data))
            .catch((err) => toast.error(formatApiError(err, "Failed to load KPIs")))
            .finally(() => setLoading(false));
    }, []);

    // Single-await async handler that uses `api.get` with
    // `responseType: "blob"` so the entire CSV download flows
    // through one awaited call. This matches the proven pattern in
    // `DataExport.jsx` (same setState-in-finally shape, lint-clean)
    // and sidesteps the React 19 `react-hooks/set-state-in-effect`
    // rule, which fires when multiple awaits surround setState
    // calls in a single async function body. Behaviour is identical:
    // server still returns `Content-Disposition: attachment; filename=…`
    // — we just read it off `response.headers` instead of the Fetch
    // Response object.
    const downloadCsv = async () => {
        setDownloading(true);
        try {
            const response = await api.get(
                "/data/admin/repository/health-report.csv",
                { responseType: "blob" },
            );
            const cd = response.headers["content-disposition"] || "";
            const m = cd.match(/filename="([^"]+)"/);
            const filename = m ? m[1] : "repository-health.csv";
            const url = URL.createObjectURL(response.data);
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`Downloaded ${filename}`);
        } catch (err) {
            toast.error(formatApiError(err, "Download failed"));
        } finally {
            setDownloading(false);
        }
    };

    // 2026-02-09 — Historical success back-stamp (Operator CR Q3).
    // Two-phase preview/apply. Auto-cohort: every active row whose
    // logo_source ∈ AUTO_ENRICHED_SOURCES and last_enrichment_result
    // is currently unset. Idempotent on re-run.
    const [backstamping, setBackstamping] = useState(false);
    const runBackstamp = async () => {
        if (backstamping) return;
        setBackstamping(true);
        try {
            // Phase 1 — preview
            const preview = await api.post(
                "/data/admin/logos/backstamp-success-marker",
                null,
                { params: { apply: false } },
            );
            const willStamp = preview.data?.will_stamp_count || 0;
            if (willStamp === 0) {
                toast.success("Nothing to back-stamp — all historical AUTO_ENRICHED rows already carry the success marker.");
                return;
            }
            if (!window.confirm(
                `Back-stamp last_enrichment_result='wrote_logo' on ${willStamp.toLocaleString()} historical rows?\n\n`
              + `These are active rows whose logo_source is auto-acquired (favicon-svg, apple-touch-icon, google-s2, google-s2-64, duckduckgo, wikipedia, homepage-scrape) but lack the post-2026-02 telemetry field.\n\n`
              + `Manual uploads and placeholder rows are NOT touched. Re-runs are no-ops.\n\n`
              + `Apply now?`
            )) return;
            // Phase 2 — apply
            const applied = await api.post(
                "/data/admin/logos/backstamp-success-marker",
                null,
                { params: { apply: true } },
            );
            const modified = applied.data?.modified || 0;
            toast.success(`Back-stamped ${modified.toLocaleString()} rows. Dashboard will refresh.`);
            // Trigger a fresh load (nocache) so the tile updates immediately.
            await load(true);
        } catch (err) {
            toast.error(formatApiError(err, "Back-stamp failed"));
        } finally {
            setBackstamping(false);
        }
    };

    if (loading && !snapshot) {
        return (
            <div className="min-h-screen bg-slate-50">
                <PageHeader title="Repository Health" description="Loading…" />
            </div>
        );
    }
    if (!snapshot) return null;

    return (
        <div className="min-h-screen bg-slate-50" data-testid="repository-dashboard">
            <PageHeader
                title="Repository Health"
                description={
                    <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
                        <StaleIndicator generatedAt={snapshot.generated_at} />
                        <span className="text-slate-300">·</span>
                        <span data-testid="dashboard-denominator-note">
                            All tiles count <strong>active</strong> repository
                            rows ({snapshot.cohort_total.toLocaleString()})
                            {population.total != null && (
                                <> — full repository has <strong>{population.total.toLocaleString()}</strong> total
                                  ({population.active?.toLocaleString()} active + {population.inactive?.toLocaleString()} inactive)</>
                            )}.
                        </span>
                    </div>
                }
                actions={
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            data-testid="refresh-kpis-button"
                            onClick={() => load(true)}
                            disabled={loading}
                            className="gap-1.5"
                        >
                            <ArrowsClockwise size={14} /> Refresh
                        </Button>
                        {can("data:read") && (
                            <Button
                                data-testid="download-csv-button"
                                onClick={downloadCsv}
                                disabled={downloading}
                                className="gap-1.5"
                            >
                                <Download size={14} weight="fill" />
                                {downloading ? "Generating…" : "Download CSV"}
                            </Button>
                        )}
                        {can("data:write") && (
                            <Button
                                variant="outline"
                                data-testid="backstamp-success-button"
                                onClick={runBackstamp}
                                disabled={backstamping}
                                title="One-shot remediation: back-stamps last_enrichment_result='wrote_logo' on historical AUTO_ENRICHED rows whose telemetry was never recorded. Idempotent."
                                className="gap-1.5"
                            >
                                <Stamp size={14} />
                                {backstamping ? "Back-stamping…" : "Back-stamp historical"}
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="px-6 sm:px-8 py-6 space-y-6">
                {/* ── Coverage ── */}
                <section data-testid="kpi-section-coverage">
                    <h2 className="font-display text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">
                        Coverage
                    </h2>
                    <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
                        <KpiTile
                            label="Active companies"
                            value={snapshot.cohort_total}
                            hint={
                                population.total != null
                                    ? `of ${population.total.toLocaleString()} in repository`
                                    : undefined
                            }
                            icon={Buildings}
                            tone="slate"
                            linkTo="/data/companies"
                            testId="kpi-total"
                            tooltip="The dashboard's operational denominator. All other coverage / review / source tiles below are computed against this active cohort. Inactive rows (e.g. matured bond issuers) are intentionally excluded from enrichment health since they don't need ongoing logo curation."
                        />
                        <KpiTile
                            label="With logos"
                            value={snapshot.has_logo.count}
                            pct={snapshot.has_logo.pct}
                            icon={Image}
                            tone="emerald"
                            linkTo="/data/companies?logo_status=has_logo"
                            testId="kpi-has-logo"
                        />
                        <KpiTile
                            label="Missing logos"
                            value={snapshot.missing_logo.count}
                            pct={snapshot.missing_logo.pct}
                            icon={ImageBroken}
                            tone="rose"
                            linkTo="/data/companies?logo_status=missing_logo"
                            testId="kpi-missing-logo"
                        />
                        <KpiTile
                            label="Letter marks"
                            value={snapshot.generated_letter_mark.count}
                            pct={snapshot.generated_letter_mark.pct}
                            hint="placeholder"
                            icon={Sparkle}
                            tone="amber"
                            linkTo="/data/companies?logo_status=generated_letter_mark"
                            testId="kpi-letter-mark"
                        />
                        <KpiTile
                            label="Never enriched"
                            value={snapshot.never_enriched.count}
                            pct={snapshot.never_enriched.pct}
                            icon={Question}
                            tone="amber"
                            linkTo="/data/companies?logo_status=never_enriched"
                            testId="kpi-never-enriched"
                        />
                    </div>
                </section>

                {/* ── Review workflow ── */}
                <section data-testid="kpi-section-review">
                    <h2 className="font-display text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">
                        Review workflow
                    </h2>
                    <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-5">
                        <KpiTile
                            label="Pending"
                            value={snapshot.review_pending.count}
                            pct={snapshot.review_pending.pct}
                            icon={Flag}
                            tone="amber"
                            linkTo="/data/companies?logo_status=review_pending"
                            testId="kpi-review-pending"
                        />
                        <KpiTile
                            label="Approved"
                            value={snapshot.review_approved.count}
                            pct={snapshot.review_approved.pct}
                            icon={CheckCircle}
                            tone="emerald"
                            linkTo="/data/companies?logo_status=review_approved"
                            testId="kpi-review-approved"
                        />
                        <KpiTile
                            label="Rejected"
                            value={snapshot.review_rejected.count}
                            pct={snapshot.review_rejected.pct}
                            icon={XCircle}
                            tone="rose"
                            linkTo="/data/companies?logo_status=review_rejected"
                            testId="kpi-review-rejected"
                        />
                        <KpiTile
                            label="Ignored"
                            value={snapshot.review_ignored.count}
                            pct={snapshot.review_ignored.pct}
                            icon={MinusCircle}
                            tone="slate"
                            linkTo="/data/companies?logo_status=review_ignored"
                            testId="kpi-review-ignored"
                        />
                        <KpiTile
                            label="Needs review total"
                            value={snapshot.needs_review.count}
                            pct={snapshot.needs_review.pct}
                            icon={Flag}
                            tone="amber"
                            linkTo="/data/companies?logo_status=needs_review"
                            testId="kpi-needs-review"
                        />
                    </div>
                </section>

                {/* ── Source breakdown + outcomes ── */}
                <section data-testid="kpi-section-sources">
                    <h2 className="font-display text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">
                        Logo source & enrichment outcomes
                    </h2>
                    <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
                        <KpiTile
                            label="Manual uploads"
                            value={snapshot.manual_upload.count}
                            pct={snapshot.manual_upload.pct}
                            icon={Image}
                            tone="indigo"
                            linkTo="/data/companies?logo_status=manual_upload"
                            testId="kpi-manual-upload"
                        />
                        <KpiTile
                            label="Auto-acquired"
                            value={snapshot.auto_enriched.count}
                            pct={snapshot.auto_enriched.pct}
                            icon={Sparkle}
                            tone="sky"
                            linkTo="/data/companies?logo_status=auto_enriched"
                            testId="kpi-auto-enriched"
                        />
                        <KpiTile
                            label="Successful enrichment writes"
                            value={snapshot.enrichment_success}
                            pct={snapshot.enrichment_success_pct}
                            icon={CheckCircle}
                            tone="emerald"
                            testId="kpi-enrich-success"
                            tooltip="Cumulative count of rows whose last_enrichment_result == 'wrote_logo'. Historical rows that landed before this telemetry was introduced (2026-02) are excluded until back-stamped — use the 'Back-stamp historical' admin action to reconcile against Auto-acquired."
                        />
                        <KpiTile
                            label="Enrichment failures"
                            value={snapshot.enrichment_failure}
                            icon={XCircle}
                            tone="rose"
                            testId="kpi-enrich-failure"
                            tooltip="Active rows whose most recent enrichment attempt's recorded result was anything other than 'wrote_logo' (no_domain, collision, third_party_portal, image_too_small, etc.)."
                        />
                    </div>
                </section>

                {/* ── Trends ── */}
                <section data-testid="kpi-section-trends">
                    <h2 className="font-display text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">
                        Trends
                    </h2>
                    <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
                        <TrendCard
                            label="New companies"
                            trend={snapshot.new_companies}
                            icon={Buildings}
                            testId="trend-new-companies"
                        />
                        <TrendCard
                            label="Logos acquired"
                            trend={snapshot.logos_acquired}
                            icon={Image}
                            testId="trend-logos-acquired"
                        />
                        <TrendCard
                            label="Enrichment attempts"
                            trend={snapshot.enrichment_attempts}
                            icon={Lightning}
                            testId="trend-enrichment-attempts"
                        />
                        <TrendCard
                            label="Review actions"
                            trend={snapshot.review_actions}
                            icon={Flag}
                            testId="trend-review-actions"
                        />
                    </div>
                </section>

                {/* ── Logo source distribution ── */}
                {snapshot.by_logo_source && Object.keys(snapshot.by_logo_source).length > 0 && (
                    <section data-testid="kpi-section-source-table">
                        <h2 className="font-display text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">
                            Source distribution
                        </h2>
                        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-left text-slate-500">
                                    <tr>
                                        <th className="px-4 py-2.5 font-medium">Source</th>
                                        <th className="px-4 py-2.5 font-medium text-right">Count</th>
                                        <th className="px-4 py-2.5 font-medium text-right">%</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100" data-testid="source-distribution-rows">
                                    {Object.entries(snapshot.by_logo_source)
                                        .sort(([, a], [, b]) => b - a)
                                        .map(([src, n]) => {
                                            const pct = snapshot.cohort_total
                                                ? (n / snapshot.cohort_total * 100).toFixed(1)
                                                : "0.0";
                                            return (
                                                <tr key={src}>
                                                    <td className="px-4 py-2.5 font-mono text-xs">{src}</td>
                                                    <td className="px-4 py-2.5 text-right font-medium">{n.toLocaleString()}</td>
                                                    <td className="px-4 py-2.5 text-right text-slate-500">{pct}%</td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
